import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';

import { useNextDocuments } from '../../hooks/useDocuments';

import { useDefaultBrand } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import PlayButton from '../buttons/PlayButton';
import DocumentsList from '../lists/DocumentsList';
import Duration from '../partials/Duration';
import Picture from '../partials/Picture';
import Video from '../partials/Video';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/documents/video-document.module.css';

export interface VideoDocumentProps {
    id?: string | null;
    video?: Video;
    image?: Image | null;
    metadata?: VideoDocumentMetadata;
    paused?: boolean;
    controls?: boolean;
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
    onComplete?: (() => void) | null;
    onProgress?: ((progress: number) => void) | null;
    className?: string | null;
}

function VideoDocument({
    id = null,
    video = null,
    image = null,
    metadata = null,
    paused = false,
    controls = true,
    current = false,
    entered = false,
    entering = false,
    leaving = false,
    onEnter = null,
    onLeave = null,
    onComplete = null,
    onProgress = null,
    className = null,
}: VideoDocumentProps) {
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [ended, setEnded] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const { brands, duration } = metadata || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle } = brand || {};
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();

    const timeoutRef = useRef(null);
    const startLeaveTimeout = useCallback(() => {
        if (onLeave === null || !current) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            onLeave();
        }, 1000);
    }, [onLeave, current]);
    const stopLeaveTimeout = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [onLeave, current]);

    const onPlay = useCallback(() => {
        setEnded(false);
        setPlaying(true);
        setHasPlayed(true);
        if (onEnter !== null && current) {
            onEnter();
        }
    }, [setPlaying, onEnter, current]);

    const onPause = useCallback(() => {
        setPlaying(false);
        startLeaveTimeout();
    }, [setPlaying, startLeaveTimeout]);

    const onMute = useCallback(() => {
        setMuted(true);
    }, [setMuted]);

    const onUnmute = useCallback(() => {
        setMuted(false);
    }, [setMuted]);

    const onEnded = useCallback(() => {
        setEnded(true);
        startLeaveTimeout();
        if (onComplete !== null) {
            onComplete();
        }
    }, [startLeaveTimeout, onComplete]);

    const onBufferStart = useCallback(() => {
        stopLeaveTimeout();
    }, [stopLeaveTimeout]);

    const onBufferEnd = useCallback(() => {
        stopLeaveTimeout();
    }, [stopLeaveTimeout]);

    const onClickVideo = useCallback(
        (newPlaying: boolean) => {
            if (newPlaying && onEnter !== null) {
                onEnter();
            }
        },
        [onEnter],
    );

    const onClickPlay = useCallback(() => {
        setPlaying(true);
    }, []);

    const onClickInfo = useCallback(() => {
        // window.scrollTo({
        //     top: 100,
        //     left: 0,
        //     behaviour: 'smooth'
        // });
        // setSidebarOpen(true);
        // if (onEnter !== null) {
        //     onEnter();
        // }
    }, [onEnter]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.hasPlayed]: hasPlayed,
                    [styles.ended]: ended,
                    [className as string]: className !== null,
                },
            ])}
        >
            {image !== null ? (
                <BasicButton
                    className={styles.start}
                    onClick={onClickPlay}
                    onPointerDown={onClickPlay}
                >
                    <Picture media={image} size="large" className={styles.poster} />
                    <PlayButton className={styles.playButton} iconClassName={styles.icon} />
                    <Duration duration={duration} timestamp className={styles.duration} />
                </BasicButton>
            ) : null}
            <div className={styles.end}>
                <SectionTitle className={styles.title}>Poursuivre l’écoute</SectionTitle>
                <DocumentsList
                    presentation="layout"
                    layout="1_2_2_2"
                    query={{
                        id,
                        type: 'video',
                    }}
                    count={7}
                    cardTheme="rounded"
                    cardWithout={['time', 'author']}
                    cell={{
                        cardType: 'vertical',
                        cardRatio: 'horizontal',
                    }}
                    smallCell={{
                        cardSize: 'small',
                    }}
                    bigCell={{
                        cardSize: 'big',
                    }}
                    clickRef="video_end"
                    hook={useNextDocuments}
                    withBorder={false}
                    className={styles.list}
                    itemsClassName={styles.items}
                    disabled={!current || id === null || !ended}
                    loading="lazy"
                />
            </div>
            {video !== null ? (
                <Video
                    media={video}
                    className={styles.video}
                    muted={muted}
                    controls={controls}
                    controlsCompact={!current || (!entered && !entering && playing) || leaving}
                    controlsTheme={brandHandle || defaultBrandHandle}
                    playing={playing && !paused}
                    nativeControls
                    withoutBlock
                    onPlay={onPlay}
                    onPause={onPause}
                    onMute={onMute}
                    onUnmute={onUnmute}
                    onProgress={onProgress}
                    onBufferStart={onBufferStart}
                    onBufferEnd={onBufferEnd}
                    onEnded={onEnded}
                    onClick={onClickVideo}
                    onClickInfo={onClickInfo}
                />
            ) : null}
        </div>
    );
}

export default VideoDocument;
